import React, { useState } from "react";
import { Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { iconForCategory } from "./categoryIcons";
import { CatNode } from "./categoryTree";

interface Props {
  tree: CatNode[];
  selected: string;          // currently selected category name (or ALL)
  allLabel: string;          // the "All Products" label
  onSelect: (name: string) => void;
}

// Resolve from the theme (not a captured BRAND value) so the selected colour
// follows the live theme palette.
const selStyle = {
  "&.Mui-selected": {
    bgcolor: "primary.main", color: "#fff",
    "&:hover": { bgcolor: "primary.dark" },
    "& .MuiListItemIcon-root": { color: "#fff" },
  },
};

const Row: React.FC<{
  node: CatNode; depth: number; selected: string; onSelect: (n: string) => void;
}> = ({ node, depth, selected, onSelect }) => {
  const hasChildren = node.children.length > 0;
  const [open, setOpen] = useState<boolean>(false); // start collapsed

  return (
    <>
      <ListItemButton
        selected={selected === node.name}
        onClick={() => onSelect(node.name)}
        sx={{ borderRadius: 1.5, mb: 0.25, pl: 1 + depth * 1.75, ...selStyle }}
      >
        {depth === 0 && (
          <ListItemIcon sx={{ minWidth: 32 }}>{iconForCategory(node.name)}</ListItemIcon>
        )}
        <ListItemText
          primaryTypographyProps={{ variant: "body2", fontWeight: depth === 0 ? 600 : 500 }}
        >
          {node.name}
        </ListItemText>
        {hasChildren && (
          <Box
            component="span"
            onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
            sx={{ display: "inline-flex", ml: 0.5, color: "inherit", cursor: "pointer" }}
          >
            {open ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
          </Box>
        )}
      </ListItemButton>

      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {node.children.map((child) => (
              <Row key={child.id} node={child} depth={depth + 1} selected={selected} onSelect={onSelect} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const CategorySidebar: React.FC<Props> = ({ tree, selected, allLabel, onSelect }) => (
  <List dense disablePadding>
    <ListItemButton
      selected={selected === allLabel}
      onClick={() => onSelect(allLabel)}
      sx={{ borderRadius: 1.5, mb: 0.25, ...selStyle }}
    >
      <ListItemIcon sx={{ minWidth: 32 }}>{iconForCategory(allLabel)}</ListItemIcon>
      <ListItemText primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}>{allLabel}</ListItemText>
    </ListItemButton>
    {tree.map((node) => (
      <Row key={node.id} node={node} depth={0} selected={selected} onSelect={onSelect} />
    ))}
  </List>
);

export default CategorySidebar;
